import { z } from 'zod';

/**
 * Campaign and Canvas monitoring (BB-107, OPS-004): a rule watches one metric of one object over a sliding window
 * and fires when it falls below / rises above a threshold or stops altogether (`absent_for`); it resolves when the
 * metric is back. Notifications go to the dashboard and, when configured, to Slack.
 */
export const ALERT_OBJECT_TYPES = ['campaign', 'canvas'] as const;
export const ALERT_METRICS = ['dispatched', 'accepted', 'failed_rate'] as const;
export const ALERT_CONDITIONS = ['below', 'above', 'absent_for'] as const;
export const ALERT_CHANNELS = ['dashboard', 'slack'] as const;

const Time = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'HH:MM');
export const ActiveHours = z.strictObject({ from: Time, to: Time, days: z.array(z.number().int().min(1).max(7)).min(1).max(7).optional() });
export type ActiveHours = z.infer<typeof ActiveHours>;

const ruleFields = {
  name: z.string().trim().min(1).max(120),
  metric: z.enum(ALERT_METRICS),
  condition: z.enum(ALERT_CONDITIONS),
  /** Count (dispatched, accepted) or share 0…1 (failed_rate); ignored by `absent_for`. */
  threshold: z.number().min(0).default(0),
  windowMinutes: z.number().int().min(5).max(24 * 60),
  activeHours: ActiveHours.nullable().default(null),
  notify: z.array(z.enum(ALERT_CHANNELS)).min(1).default(['dashboard']),
  enabled: z.boolean().default(true),
};

export const CreateAlertRuleBody = z
  .strictObject({ objectType: z.enum(ALERT_OBJECT_TYPES), objectId: z.uuid(), ...ruleFields })
  .refine((r) => r.metric !== 'failed_rate' || r.threshold <= 1, { message: 'failed_rate is a share from 0 to 1', path: ['threshold'] });
export type CreateAlertRuleBody = z.infer<typeof CreateAlertRuleBody>;

export const UpdateAlertRuleBody = z.strictObject({
  name: ruleFields.name.optional(),
  threshold: z.number().min(0).optional(),
  windowMinutes: ruleFields.windowMinutes.optional(),
  activeHours: ActiveHours.nullable().optional(),
  notify: z.array(z.enum(ALERT_CHANNELS)).min(1).optional(),
  enabled: z.boolean().optional(),
});
export const AlertRuleQuery = z.object({ objectType: z.enum(ALERT_OBJECT_TYPES).optional(), objectId: z.uuid().optional() });

export interface AlertRuleView extends Omit<CreateAlertRuleBody, never> {
  id: string;
  state: 'ok' | 'firing';
  lastEvaluatedAt: string | null;
  lastValue: number | null;
  createdBy: { id: string; email: string | null } | null;
  updatedAt: string;
}

export interface AlertEventView {
  id: string;
  ruleId: string;
  ruleName: string;
  objectType: (typeof ALERT_OBJECT_TYPES)[number];
  objectId: string;
  value: number;
  threshold: number;
  firedAt: string;
  resolvedAt: string | null;
  notified: string[];
}
